import React from 'react';

import get from 'lodash/get';
import set from 'lodash/set';
import isNull from 'lodash/isNull';
import isEmpty from 'lodash/isEmpty';

import Switch from 'react-bootstrap-switch';
import LocalizableSelect from 'PMT/components/form/LocalizableSelect';
import { TextInput, NumberInput, PdfDropzone } from 'PMT/components/form';

const FormGroup = ({ label, style, children }) => (
  <div className="row form-group" style={style}>
    <div className="col-xs-4 control-label">
      <label>{label}</label>
    </div>

    <div className="col-xs-6">{children}</div>
  </div>
);

const ChangeStatusView = ({ status, pspSettings, bank, legal, onChange }) => {
  // status is the current status, not the modified one.
  // retrieve the status from the state
  const statusToSet = get(pspSettings, 'payGreen.status');

  if (status !== statusToSet) {
    return (
      <div>
        <p>Le status va être modifié pour: {statusToSet}</p>

        <button
          className="btn"
          onClick={event => {
            event.preventDefault();
            onChange(status);
          }}
        >
          Ne pas modifier le status
        </button>
      </div>
    );
  }

  switch (status) {
    default:
    case 'WAITING_CONFIGURATION':
      // We must configure the psp settings and ask to create the shop using TO_CREATE.
      let isValidToCreate = true;

      if (isEmpty(legal.siren)) {
        isValidToCreate = false;
      }

      const acquisitionType = get(pspSettings, 'payGreen.acquisitionType', null);
      if (isNull(acquisitionType)) {
        isValidToCreate = false;
      }

      if (acquisitionType === 'EXTERNAL_BANK') {
        if (
          isEmpty(
            get(pspSettings, 'payGreen.paylineConfiguration.vadNumber', null)
          )
        ) {
          isValidToCreate = false;
        }

        if (
          isEmpty(
            get(pspSettings, 'payGreen.paylineConfiguration.mccNumber', null)
          )
        ) {
          isValidToCreate = false;
        }

        if (
          isEmpty(
            get(pspSettings, 'payGreen.paylineConfiguration.maxAmount', null)
          )
        ) {
          isValidToCreate = false;
        }
      }

      return (
        <div>
          {isValidToCreate ? (
            <button
              className="btn btn-primary"
              onClick={event => {
                event.preventDefault();
                onChange('TO_CREATE');
              }}
            >
              Créer la boutique chez PayGreen
            </button>
          ) : (
            <div>
              Il manque des informations pour créer la boutique. Sont requis:
              <ul>
                <li>Le mode (avec wallet packagé ou banque externe)</li>
                <li>Si banque externe, la configuration</li>
                <li>Le siren</li>
              </ul>
            </div>
          )}
        </div>
      );

    case 'CREATED':
      return (
        <div>
          <p>La boutique a bien été créée chez PayGreen. L'activer ?</p>

          <p>
            Attention: Pour activer la boutique chez PayGreen, vous devez avoir
            reçu la confirmation par email de PayGreen: la boutique doit être
            correctement configurée manuellement de leur coté.
          </p>

          <button
            className="btn btn-danger"
            onClick={event => {
              event.preventDefault();
              onChange('ENABLED');
            }}
          >
            Activer la boutique chez PayGreen
          </button>
        </div>
      );

    case 'ENABLED':
      return (
        <div>
          <p>Attention: Vous devriez également désactiver le restaurant</p>

          <button
            className="btn btn-danger"
            onClick={event => {
              event.preventDefault();
              onChange('DISABLED');
            }}
          >
            Désactiver la boutique
          </button>
        </div>
      );

    case 'DISABLED':
      return (
        <div>
          <button
            className="btn btn-danger"
            onClick={event => {
              event.preventDefault();
              onChange('ENABLED');
            }}
          >
            Activer la boutique
          </button>
        </div>
      );
  }
};

const PayGreenConfigurationView = ({
  pspSettings,
  restaurant,
  bank,
  legal,
  onChange,
  onChangeBank,
}) => {
  // we use the current data, not the modified one
  const status = get(restaurant, 'pspSettings.payGreen.status', null);

  const isStatusWaitingConfiguration = status === 'WAITING_CONFIGURATION';

  return (
    <div className="top-buffer-50">
      <h3 className="text-center" style={{ marginBottom: 50 }}>
        PayGreen configuration - General
      </h3>

      {/* status */}
      <FormGroup label="Status">
        <TextInput value={status} readOnly />
      </FormGroup>

      <FormGroup label="Changement du status">
        <ChangeStatusView
          status={status}
          onChange={status => {
            onChange(set(pspSettings, 'payGreen.status', status));
          }}
          pspSettings={pspSettings}
          bank={bank}
          legal={legal}
        />
      </FormGroup>

      {/* shop id */}
      <FormGroup label="shop id" style={{ marginTop: 50 }}>
        <TextInput
          value={get(pspSettings, 'payGreen.shopId', null)}
          readOnly={!isStatusWaitingConfiguration}
          onChange={value => {
            onChange(set(pspSettings, 'payGreen.shopId', value));
          }}
        />
      </FormGroup>

      {/* shop private key */}
      <FormGroup label="shop private key">
        <TextInput
          value={get(pspSettings, 'payGreen.shopPrivateKey', null)}
          readOnly={!isStatusWaitingConfiguration}
          onChange={value => {
            onChange(set(pspSettings, 'payGreen.shopPrivateKey', value));
          }}
        />
      </FormGroup>

      {/* wallet ou banque externe */}
      <FormGroup label="Avec banque externe ? (Désactivé: avec wallet)">
        <Switch
          value={get(pspSettings, 'payGreen.acquisitionType', null) === 'EXTERNAL_BANK'}
          disabled={!isStatusWaitingConfiguration}
          onChange={() => {
            onChange(
              set(
                pspSettings,
                'payGreen.acquisitionType',
                get(pspSettings, 'payGreen.acquisitionType', null) === 'EXTERNAL_BANK'
                  ? 'PACKAGED_WALLET'
                  : 'EXTERNAL_BANK'
              )
            );
          }}
          bsSize="mini"
        />
      </FormGroup>

      {/* VAD configuration */}
      {get(pspSettings, 'payGreen.acquisitionType', null) === 'EXTERNAL_BANK' && (
        <FormGroup label="Configuration de la banque externe - vad number">
          <TextInput
            value={get(
              pspSettings,
              'payGreen.paylineConfiguration.vadNumber',
              null
            )}
            readOnly={!isStatusWaitingConfiguration}
            onChange={value => {
              onChange(
                set(
                  pspSettings,
                  'payGreen.paylineConfiguration.vadNumber',
                  value
                )
              );
            }}
          />
        </FormGroup>
      )}

      {get(pspSettings, 'payGreen.acquisitionType', null) === 'EXTERNAL_BANK' && (
        <FormGroup label="Configuration de la banque externe - mcc number">
          <TextInput
            value={get(
              pspSettings,
              'payGreen.paylineConfiguration.mccNumber',
              null
            )}
            readOnly={!isStatusWaitingConfiguration}
            onChange={value => {
              onChange(
                set(
                  pspSettings,
                  'payGreen.paylineConfiguration.mccNumber',
                  value
                )
              );
            }}
          />
        </FormGroup>
      )}

      {get(pspSettings, 'payGreen.acquisitionType', null) === 'EXTERNAL_BANK' && (
        <FormGroup label="Configuration de la banque externe - max amount">
          <TextInput
            value={get(
              pspSettings,
              'payGreen.paylineConfiguration.maxAmount',
              null
            )}
            readOnly={!isStatusWaitingConfiguration}
            onChange={value =>
              onChange(
                set(
                  pspSettings,
                  'payGreen.paylineConfiguration.maxAmount',
                  value
                )
              )
            }
          />
        </FormGroup>
      )}
    </div>
  );
};

export default PayGreenConfigurationView;



// WEBPACK FOOTER //
// ./scripts/pages/restaurant/edit/components/PayGreenConfigurationView.js